import React from 'react';
import { Box, Text, Heading, Link, VStack } from '@chakra-ui/react';

interface MarkdownContentProps {
  content: string;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content }) => {
  const renderContent = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === '') {
        elements.push(<Box key={key++} h={4} />);
        continue;
      }

      // Handle headings
      if (line.startsWith('## ')) {
        elements.push(
          <Heading
            key={key++}
            as="h2"
            fontSize="24px"
            fontWeight="bold"
            color="text.darkBlue.900"
            mb={4}
            mt={8}
            fontFamily="marketingPage">
            {line.replace('## ', '')}
          </Heading>,
        );
        continue;
      }

      // Handle headings
      if (line.startsWith('### ')) {
        elements.push(
          <Heading
            key={key++}
            as="h3"
            fontSize="20px"
            fontWeight="bold"
            color="text.darkBlue.900"
            mb={3}
            mt={6}
            fontFamily="marketingPage">
            {line.replace('### ', '')}
          </Heading>,
        );
        continue;
      }

      // Handle list items
      if (line.startsWith('- ')) {
        const listItems: string[] = [];
        let j = i;
        while (j < lines.length && lines[j].trim().startsWith('- ')) {
          listItems.push(lines[j].trim().replace('- ', ''));
          j++;
        }
        i = j - 1;

        elements.push(
          <Box key={key++} as="ul" mb={4} pl={6}>
            {listItems.map((item, idx) => (
              <Text key={idx} as="li" mb={2} fontSize="18px" lineHeight="1.7">
                {item}
              </Text>
            ))}
          </Box>,
        );
        continue;
      }

      // Handle numbered lists
      if (/^\d+\. /.test(line)) {
        const listItems: string[] = [];
        let j = i;
        while (j < lines.length && /^\d+\. /.test(lines[j].trim())) {
          listItems.push(lines[j].trim().replace(/^\d+\. /, ''));
          j++;
        }
        i = j - 1;

        elements.push(
          <Box key={key++} as="ol" mb={4} pl={6}>
            {listItems.map((item, idx) => (
              <Text key={idx} as="li" mb={2} fontSize="18px" lineHeight="1.7">
                {item}
              </Text>
            ))}
          </Box>,
        );
        continue;
      }

      // Handle regular paragraphs with links
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      const parts = line.split(linkRegex);
      const paragraphElements: React.ReactNode[] = [];

      for (let k = 0; k < parts.length; k += 3) {
        if (parts[k]) {
          paragraphElements.push(parts[k]);
        }
        if (parts[k + 1] && parts[k + 2]) {
          paragraphElements.push(
            <Link
              key={`link-${k}`}
              href={parts[k + 2]}
              color="blue.500"
              fontWeight="medium"
              _hover={{ textDecoration: 'underline' }}
              isExternal={!parts[k + 2].startsWith('/')}>
              {parts[k + 1]}
            </Link>,
          );
        }
      }

      elements.push(
        <Text key={key++} fontSize="18px" lineHeight="1.7" mb={4}>
          {paragraphElements}
        </Text>,
      );
    }

    return elements;
  };

  return (
    <VStack align="stretch" spacing={0}>
      {renderContent(content)}
    </VStack>
  );
};

export default MarkdownContent;
